import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowUpRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { SERVICE_FILTER_OPTIONS } from "@/data/content";

const inputCls = "w-full rounded-xl border border-white/10 bg-[#0F1420] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5";

export default function LeadForm({ compact = false }) {
    const [params] = useSearchParams();
    const [settings, setSettings] = useState(null);
    const [form, setForm] = useState({
        full_name: "", email: "", company: "", phone: "",
        service: params.get("service") || "", budget: "", timeline: "",
        description: "", preferred_contact: "", privacy_ack: false, website: "",
    });
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle"); // idle | sending | done
    const [reference, setReference] = useState("");

    useEffect(() => {
        api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const s = params.get("service");
        if (s && SERVICE_FILTER_OPTIONS.includes(s)) setForm((f) => ({ ...f, service: s }));
    }, [params]);

    const set = (k) => (e) => {
        const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const validate = () => {
        const er = {};
        if (form.full_name.trim().length < 2) er.full_name = "Please enter your full name.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid business email.";
        if (!form.service) er.service = "Please choose a service.";
        if (form.description.trim().length < 10) er.description = "Tell us a little more about your project (10+ characters).";
        if (!form.privacy_ack) er.privacy_ack = "Please acknowledge the privacy notice.";
        setErrors(er);
        return Object.keys(er).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setStatus("sending");
        try {
            const { data } = await api.post("/inquiries", {
                ...form,
                referrer_page: window.location.pathname,
            });
            setReference(data.reference);
            setStatus("done");
            if (data.duplicate) toast.info("We already have your recent inquiry — reference below.");
        } catch (err) {
            setStatus("idle");
            toast.error(errMsg(err));
        }
    };

    if (status === "done") {
        return (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-8 text-center" data-testid="inquiry-success-message">
                <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
                <h3 className="mt-4 font-display text-2xl font-bold text-white">Inquiry received.</h3>
                <p className="mt-2 text-slate-300 text-sm leading-relaxed">
                    Thank you, {form.full_name.split(" ")[0]}. Your project inquiry has been saved and our team will review it during business hours (Mon-Fri, 9 AM - 5 PM PT).
                </p>
                <p className="mt-4 inline-block rounded-lg border border-white/10 bg-black/30 px-4 py-2 font-mono2 text-sm text-amber-300" data-testid="inquiry-reference-number">
                    Reference: {reference}
                </p>
                <p className="mt-3 text-xs text-slate-500">Keep this reference number for any follow-up about your inquiry.</p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} noValidate className="space-y-5" data-testid="inquiry-form">
            <div className={compact ? "space-y-5" : "grid gap-5 sm:grid-cols-2"}>
                <div>
                    <label htmlFor="lf-name" className={labelCls}>Full Name *</label>
                    <input id="lf-name" data-testid="inquiry-name-input" className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Jane Cooper" autoComplete="name" />
                    {errors.full_name && <p className="mt-1 text-xs text-rose-400" data-testid="inquiry-name-error">{errors.full_name}</p>}
                </div>
                <div>
                    <label htmlFor="lf-email" className={labelCls}>Business Email *</label>
                    <input id="lf-email" type="email" data-testid="inquiry-email-input" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@company.com" autoComplete="email" />
                    {errors.email && <p className="mt-1 text-xs text-rose-400" data-testid="inquiry-email-error">{errors.email}</p>}
                </div>
                <div>
                    <label htmlFor="lf-company" className={labelCls}>Company / Business Name</label>
                    <input id="lf-company" data-testid="inquiry-company-input" className={inputCls} value={form.company} onChange={set("company")} placeholder="Company Inc." autoComplete="organization" />
                </div>
                <div>
                    <label htmlFor="lf-phone" className={labelCls}>Telephone</label>
                    <input id="lf-phone" type="tel" data-testid="inquiry-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(562) 555-0100" autoComplete="tel" />
                </div>
                <div>
                    <label htmlFor="lf-service" className={labelCls}>Service of Interest *</label>
                    <select id="lf-service" data-testid="inquiry-service-select" className={inputCls} value={form.service} onChange={set("service")}>
                        <option value="">Select a service…</option>
                        {SERVICE_FILTER_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {errors.service && <p className="mt-1 text-xs text-rose-400" data-testid="inquiry-service-error">{errors.service}</p>}
                </div>
                <div>
                    <label htmlFor="lf-budget" className={labelCls}>Project Budget</label>
                    <select id="lf-budget" data-testid="inquiry-budget-select" className={inputCls} value={form.budget} onChange={set("budget")}>
                        <option value="">Prefer not to say</option>
                        {(settings?.budget_options || []).map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="lf-timeline" className={labelCls}>Desired Timeline</label>
                    <select id="lf-timeline" data-testid="inquiry-timeline-select" className={inputCls} value={form.timeline} onChange={set("timeline")}>
                        <option value="">Not sure yet</option>
                        {(settings?.timeline_options || []).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div>
                    <label htmlFor="lf-contact-method" className={labelCls}>Preferred Contact Method</label>
                    <select id="lf-contact-method" data-testid="inquiry-contact-method-select" className={inputCls} value={form.preferred_contact} onChange={set("preferred_contact")}>
                        <option value="">No preference</option>
                        <option value="Email">Email</option>
                        <option value="Phone">Phone</option>
                    </select>
                </div>
            </div>
            <div>
                <label htmlFor="lf-description" className={labelCls}>Project Description *</label>
                <textarea id="lf-description" rows={compact ? 4 : 5} data-testid="inquiry-description-input" className={inputCls} value={form.description} onChange={set("description")} placeholder="Tell us about your business, your goals, and what you would like us to build or create." />
                {errors.description && <p className="mt-1 text-xs text-rose-400" data-testid="inquiry-description-error">{errors.description}</p>}
            </div>
            {/* Honeypot — hidden from humans */}
            <div className="hidden" aria-hidden="true">
                <label htmlFor="lf-website">Website</label>
                <input id="lf-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
            </div>
            <div>
                <label className="flex items-start gap-3 cursor-pointer" htmlFor="lf-privacy">
                    <input id="lf-privacy" type="checkbox" data-testid="inquiry-privacy-checkbox" checked={form.privacy_ack} onChange={set("privacy_ack")}
                        className="mt-1 h-4 w-4 rounded border-white/20 bg-[#0F1420] accent-violet-600" />
                    <span className="text-xs text-slate-400 leading-relaxed">
                        I acknowledge that the information I submit will be used by Innovative Fusion Hives solely to respond to my inquiry, as described in the privacy notice. *
                    </span>
                </label>
                {errors.privacy_ack && <p className="mt-1 text-xs text-rose-400" data-testid="inquiry-privacy-error">{errors.privacy_ack}</p>}
            </div>
            <button type="submit" disabled={status === "sending"} data-testid="inquiry-submit-button"
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-violet-600 px-8 py-3.5 font-semibold text-white transition-all hover:bg-violet-500 hover:shadow-[0_0_32px_rgba(124,58,237,0.5)] disabled:opacity-60 disabled:cursor-not-allowed">
                {status === "sending" ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                ) : (
                    <>Send Project Inquiry <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>
                )}
            </button>
            <p className="text-[11px] text-slate-500 leading-relaxed">
                Privacy notice: your details are stored securely and used only to respond to your inquiry. They are never sold or shared with third parties for marketing.
            </p>
        </form>
    );
}
